<script setup lang="ts">
  import { useDocumentVisibility } from '@vueuse/core'
  import { useReadItems } from '../../../composables/useReadItems'
  import { useArticle } from '../../../composables/data-fetching/useArticle/useArticle'

  const documentVisibility = useDocumentVisibility()
  const { selectedCategory } = useSearchParams()
  const { trackReadItem } = useReadItems()

  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)

  const pageDesign = computed(
    () => featureFlags?.value.summaryPage?.design ?? 'google'
  )

  const route = useRoute()
  const articleId = Array.isArray(route.params.id)
    ? route.params.id[0]
    : route.params.id
  const { article, pending, error } = await useArticle(articleId)

  if (!pending.value && !article.value && error) {
    returnToHomePage(selectedCategory.value)
  }

  async function returnToHomePage(category: string) {
    const { settings } = useLayoutSettings()
    await navigateTo({
      path: settings.homePageUrl,
      query: { category },
    })
  }

  const { settings } = useLayoutSettings()
  const siteDomain = settings.siteName.toLowerCase()
  const title = article?.value?.title || `Article - ${settings.siteName}`
  const description =
    article?.value?.subtitle || `Read more about ${article?.value?.title}.`
  const { track } = useAnalytics()

  useSeoMeta({
    title: title,
    ogTitle: title,
    ogDescription: description,
    description: description,
    ogImage: article?.value?.thumbnailUrl || settings.headerLogo,
    ogUrl: `https://${siteDomain}.com/article/${article?.value?.id}`,
    ogType: 'article',
    articleAuthor: article?.value?.author ? [article.value.author] : [],
    articlePublishedTime: article?.value?.publishedDate || '',
  })

  watch(
    () => featureFlags.value.enableArticleReordering,
    (newValue) => {
      if (newValue) {
        trackReadItem(articleId, documentVisibility)
      }
    },
    { immediate: true }
  )
  await contentDisplaySettingsStore.initialized
</script>
<template>
  <LayoutsSummary>
    <template #article-summary-header>
      <ArticleHeader
        v-if="article"
        :title="article.title"
        :publisher-icon-url="article.providerLogoUrl || article.favicon"
        :publisher-name="article.provider"
        :published-date="article.publishedDate"
        :category="
          article.category?.includes('NC') ? 'News' : article?.category
        "
        :design="pageDesign"
      />
    </template>
    <template #article-summary-body>
      <ArticleSummary
        v-if="article"
        :article-image="article.thumbnailUrl ?? ''"
        :article-url="article.link"
        :context="article.summary"
        :topic-bullets="article.keypoints.split('\n')"
        :design="pageDesign"
        @original-news-button-click="
          track('NtpOriginalNewsButton_Clicked', {
            ntp_news_category: article.category,
            ntp_news_provider: 'duplex',
          })
        "
      />
    </template>
  </LayoutsSummary>
</template>
